import {
  catchError,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { computed, inject, Injectable, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { QRWebsocketEvents, QRWebsocketService } from './qr-websocket.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Language } from '@app/shared/models/app.model';
import { LanguageSettingsService } from './language-settings.service';
import { licenseDeviceUuid } from './transcript/license-device-uuid';
import { QrLanguageDialogComponent } from '../components/qr-language-dialog/qr-language-dialog.component';
import { Router } from '@angular/router';
import { SettingsService } from './settings.service';
import { SharedSessionAuthService } from './shared-session-auth.service';
import { SpeakseeOrderService } from '@app/modules/license/license.service';
import { ToastrService } from 'ngx-toastr';
import { TRANSLATION_BUFFER_MS } from '@app/directives/stable-text/delayed-typing.config';
import { TtsAudioService } from './tts-audio.service';
import { UpdateMicrophoneSettingsRequest } from './settings-api.service';

@Injectable({
  providedIn: 'root',
})
export class GuestSessionService {
  private readonly QR_LANGUAGE_PREFERENCE_KEY = 'qr_user_language_preference';
  private readonly QR_DEVICE_UUID_KEY = 'qr_device_uuid';
  private readonly QR_DEVICE_TYPE_KEY = 'qr_device_type';



  private isQrUserShown = signal(false);
  private isDialogCurrentlyOpen = signal(false);
  private websocketInitialized = signal(false);
  private qrLanguageDialogRef: MatDialogRef<QrLanguageDialogComponent> | null = null;

  private initialization$: Observable<void> | null = null;

  // Use null to represent "unknown" state - only set to false when explicitly disabled
  public readonly guestTranslationsEnabled = signal<boolean | null>(null);
  public readonly supportedLanguages = signal<Language[]>([]);

  // TTS read-along availability for this device's viewers, pushed on connect as
  // the device setting guest.features.tts.enabled (GUEST_FEATURES_TTS event).
  // null = unknown until the push arrives; opt-in, so only an explicit true enables it.
  public readonly guestTtsEnabled = signal<boolean | null>(null);
  /**
   * TTS read-along is enabled when the device's tts setting is on
   * (guest.features.tts.enabled, pushed on connect) OR a developer has
   * force-enabled it through the developer menu (tap the logo to open the
   * dialog). Reactive to both so the header toggle appears the moment either flips.
   */
  public readonly ttsReadAlongEnabled = computed(
    () => this.ttsDevOverride() || this.guestTtsEnabled() === true
  );

  public readonly translationBufferMs = signal<number | null>(null);
  public readonly effectiveBufferMs = computed(() => {
    if (!this.isSoundshapeQR()) return 0;
    const ms = this.translationBufferMs();
    return ms !== null ? ms : TRANSLATION_BUFFER_MS;
  });

  // Track if transcript retry has been attempted
  private transcriptRetryAttempted = signal<boolean>(false);

  private dialog = inject(MatDialog);
  private languageSettings = inject(LanguageSettingsService);
  private settingsService = inject(SettingsService);
  private qrWebsocketService = inject(QRWebsocketService);
  private orderService = inject(SpeakseeOrderService);
  private ttsAudioService = inject(TtsAudioService);
  private router = inject(Router);
  private sharedSessionAuth = inject(SharedSessionAuthService);
  private toastr = inject(ToastrService);

  // Developer-menu override for TTS read-along, persisted in local settings and
  // toggled from the developer dialog. Mirrored as a signal (localSettings$ is a
  // BehaviorSubject, so it emits synchronously) so `ttsReadAlongEnabled` reacts
  // when it is flipped on.
  private readonly ttsDevOverride = toSignal(
    this.settingsService.localSettings$.pipe(map((s) => s.ttsReadAlong ?? false)),
    { requireSync: true }
  );

  constructor() {
    this.qrWebsocketService.incomingMessages
      .pipe(takeUntilDestroyed())
      .subscribe((message) => {
        try {
          switch (message.event) {
            case QRWebsocketEvents.GUEST_FEATURES_TRANSLATIONS:
              this.handleGuestFeaturesTranslations(message.payload);
              break;
            case QRWebsocketEvents.GUEST_FEATURES_TTS:
              this.handleGuestFeaturesTts(message.payload);
              break;
            case QRWebsocketEvents.GUEST_TRANSLATIONS_SUPPORTED_LANGUAGES:
              this.handleSupportedLanguages(message.payload);
              break;
            case QRWebsocketEvents.SPEECH_SDK_RECOGNITION_LANGUAGE_UPDATED:
            case QRWebsocketEvents.SPEECH_SDK_RECOGNITION_LANGUAGE_RESULT:
              this.handleRecognitionLanguageUpdated(message.payload);
              break;
            case QRWebsocketEvents.GUEST_TEXT_BEHAVIOR:
              this.handleTextBehavior(message.payload);
              break;
            case QRWebsocketEvents.SESSION_ENDED_NOTIFICATION:
              this.handleSessionEnded(message.payload);
              break;
          }

          // Handle translation delay broadcasts (event name varies by display/output view)
          if (message.event && QRWebsocketEvents.TEXT_BEHAVIOR_UPDATED.test(message.event)) {
            this.handleTextBehavior(message.payload);
          }
        } catch (error) {
          console.error('[QR User Service] Error processing WebSocket message:', error, message);
        }
      });

  }

  /**
   * Host ended the session. Only meaningful for shared-session guests.
   */
  private handleSessionEnded(_payload: any): void {
    if (!this.isSharedSessionUser()) return;
    // Capture device uuid before flushing so the ended view can still show tenant branding.
    const deviceUuid = this.sharedSessionAuth.getDeviceUuid();
    this.sharedSessionAuth.cancelRefresh();
    this.sharedSessionAuth.flushTokens();
    this.disconnectWebsocket();
    this.toastr.info('The host ended this session.', 'Session ended');
    const queryParams: Record<string, string> = { ended: '1' };
    if (deviceUuid) queryParams['device'] = deviceUuid;
    void this.router.navigate(['/join'], { queryParams });
  }

  /**
   * Handle text behavior event (proactive on auth + broadcast on update)
   */
  private handleTextBehavior(payload: any): void {
    const bufferMs = payload?.translation_buffer_ms;
    this.translationBufferMs.set(typeof bufferMs === 'number' ? bufferMs : null);
  }

  /**
   * Handle guest features translations event
   */
  private handleGuestFeaturesTranslations(payload: any): void {
    if (payload && typeof payload.enabled === 'boolean') {
      this.guestTranslationsEnabled.set(payload.enabled);

      // If translations are disabled, close the QR language dialog if it's open
      if (!payload.enabled && this.qrLanguageDialogRef) {
        this.qrLanguageDialogRef.close();
        this.qrLanguageDialogRef = null;
      }
      // Note: language-setting-dialog stays open - language_out will be disabled via translationsDisabled signal
    }
  }

  /**
   * Handle guest features tts ("read-along") event. The device setting
   * guest.features.tts.enabled gates whether read-along is offered to viewers.
   * Absent/non-boolean payload leaves the signal null (treated as off — opt-in).
   */
  private handleGuestFeaturesTts(payload: any): void {
    if (payload && typeof payload.enabled === 'boolean') {
      this.guestTtsEnabled.set(payload.enabled);
      // If read-along is no longer available (device turned TTS off and no
      // developer override), tear down any active subscription so the proxy
      // receives tts.read_along.unsubscribe. Idempotent when already off.
      if (!this.ttsReadAlongEnabled()) {
        this.ttsAudioService.setEnabled(false);
      }
    }
  }

  /**
   * Handle supported languages event
   */
  private handleSupportedLanguages(payload: any): void {
    if (!payload?.data || !Array.isArray(payload.data)) {
      console.warn('[QR User Service] Invalid supported languages payload:', payload);
      return;
    }

    const allKnownLanguages = this.languageSettings.getAllLanguages();

    const languages: Language[] = payload.data.map((lang: {
      code: string;
      display_name?: string;
      tts_available?: boolean;
    }) => {
      // Whether read-along can speak this locale. A proxy that predates the
      // flag omits it, and the pre-flag behaviour was to offer everything, so
      // only an explicit false withholds a language here.
      const ttsAvailable = lang.tts_available !== false;

      // Try to find in known languages first to get correct icons and names
      const match = allKnownLanguages.find(l => l.languageCode === lang.code);
      if (match) {
        // Copied, not returned as-is: these objects are shared with the
        // app-wide language list, and attaching the flag to them would leak
        // one device's read-along availability into every other picker.
        return { ...match, ttsAvailable };
      }

      // Fallback for unknown languages
      const countryCode = lang.code?.includes('-')
        ? lang.code.split('-')[1].toLowerCase()
        : (lang.code || '').toLowerCase();
      console.warn(`[QR User Service] No match for ${lang.code}, using fallback`);
      return {
        languageCode: lang.code,
        exonym: lang.display_name || lang.code,
        language: lang.display_name || lang.code,
        iconClass: `fi fi-${countryCode}`,
        isVerified: true,
        ttsAvailable,
      };
    });

    this.supportedLanguages.set(languages);

    // Show dialog if WebSocket is initialized and dialog hasn't been shown yet
    if (this.websocketInitialized() && !this.isQrUserShown() && !this.isDialogCurrentlyOpen()) {
      // Use setTimeout to avoid synchronous signal updates
      setTimeout(() => {
        this.showLanguageSelectionForQrUser();
      }, 0);
    }
  }

  /**
   * Handle recognition language updated event
   * Updates the local microphone language_in when the backend changes the recognition language
   */
  private handleRecognitionLanguageUpdated(payload: any): void {
    if (!Array.isArray(payload) || payload.length === 0) {
      console.warn('[QR User Service] Invalid recognition language payload:', payload);
      return;
    }

    const languageCode = payload[0];
    if (typeof languageCode !== 'string') {
      console.warn('[QR User Service] Invalid recognition language code:', languageCode);
      return;
    }

    this.settingsService.updateLocalMicLanguageIn(languageCode);
  }

  /**
   * Checks if the current user is a QR user
   * @returns true if the user authenticated via QR code
   */
  isQrUser(): boolean {
    return sessionStorage.getItem('auth_mode') === 'qr';
  }

  /**
   * Checks if the current user is a shared-session guest
   */
  isSharedSessionUser(): boolean {
    return this.sharedSessionAuth.isActive();
  }

  /**
   * Returns true for any guest auth flow (QR or shared-session)
   */
  isGuestUser(): boolean {
    return this.isQrUser() || this.isSharedSessionUser();
  }

  /**
   * Shows the QR onboarding dialog (step 1: listen/read, step 2: language).
   * Step 2 is skipped inside the dialog when a saved language preference exists.
   */
  async showLanguageSelectionForQrUser(): Promise<Language | undefined> {
    if (!this.isGuestUser()) {
      return undefined;
    }

    if (this.guestTranslationsEnabled() === false) {
      return undefined;
    }

    if (this.isDialogCurrentlyOpen() || this.isQrUserShown()) {
      return undefined;
    }

    const savedLanguageCode = this.getLanguagePreference();
    const availableLanguages = this.supportedLanguages().length > 0
      ? this.supportedLanguages()
      : this.languageSettings.getAllLanguages();
    const savedLanguage =
      (savedLanguageCode && availableLanguages.find((l) => l.languageCode === savedLanguageCode)) || null;

    // Devices outside the TTS rollout list keep the pre-TTS flow: a saved
    // preference is applied silently with no dialog at all; first-time
    // users get the plain language picker (the dialog skips its listen/read
    // step when ttsEnabled is false).
    const ttsEnabled = this.ttsReadAlongEnabled();
    if (!ttsEnabled && savedLanguage) {
      this.saveQrUserLanguage(savedLanguage).pipe(take(1)).subscribe();
      return savedLanguage;
    }

    this.isQrUserShown.set(true);
    this.setupTranslationsForDialog();

    try {
      this.qrLanguageDialogRef = this.dialog.open(QrLanguageDialogComponent, {
        maxWidth: '720px',
        width: '92vw',
        maxHeight: '90vh',
        disableClose: true,
        autoFocus: false,
        panelClass: 'qr-language-dialog',
        data: {
          languages: this.supportedLanguages(),
          savedLanguage,
          ttsEnabled,
        },
      });

      this.isDialogCurrentlyOpen.set(true);

      const selectedLanguage = await firstValueFrom(this.qrLanguageDialogRef.afterClosed());
      return selectedLanguage;
    } catch (error) {
      console.error('[QR User Service] Error showing language dialog:', error);
      return undefined;
    } finally {
      this.isDialogCurrentlyOpen.set(false);
      this.qrLanguageDialogRef = null;
    }
  }

  /**
   * Saves the QR user's language preference to localStorage
   * @param languageCode The language code to save
   */
  saveLanguagePreference(languageCode: string): void {
    if (!this.isGuestUser()) return;
    localStorage.setItem(this.QR_LANGUAGE_PREFERENCE_KEY, languageCode);
  }

  /**
   * Gets the QR user's saved language preference from localStorage
   * @returns The saved language code or null if none exists
   */
  getLanguagePreference(): string | null {
    if (!this.isGuestUser()) return null;
    return localStorage.getItem(this.QR_LANGUAGE_PREFERENCE_KEY);
  }

  /**
   * Send target language update via WebSocket
   * Handles microphone kit vs soundshape logic internally
   * @param languageCode The BCP 47 language code
   * @param micId Optional mic_id for single microphone update (only used when updateMode is 'each')
   * @param updateMode 'all' to update all microphones, 'each' to update single microphone
   * @returns Observable that completes after sending WebSocket messages
   */
  sendTargetLanguageUpdate(
    languageCode: string,
    micId?: number,
    updateMode: 'all' | 'each' = 'all'
  ): Observable<void> {
    if (!this.isSoundshapeQR()) {
      return of(undefined);
    }

    this.transcriptRetryAttempted.set(false);

    return this.settingsService.getRemoteSetSettings().pipe(
      take(1),
      map((settings) => {
        const microphones = settings?.microphones ?? [];
        const isMicrophoneKit = microphones.length > 1;

        // Soundshape: single message without input_uuid
        if (!isMicrophoneKit) {
          this.qrWebsocketService.setGuestTargetLanguages([languageCode]);
          this.settingsService.updateLocalMicLanguageOut(languageCode);
          return;
        }

        // Microphone kit: update all mics
        if (updateMode === 'all') {
          microphones.forEach((mic) => {
            this.qrWebsocketService.setGuestTargetLanguages([languageCode], mic.uuid);
          });
          this.settingsService.updateLocalMicLanguageOut(languageCode);
          return;
        }

        // Microphone kit: update single mic
        const mic = microphones.find((m) => m.mic_id === micId);
        if (!mic?.uuid) {
          console.warn('[QR User Service] Microphone not found for mic_id:', micId);
          return;
        }
        this.qrWebsocketService.setGuestTargetLanguages([languageCode], mic.uuid);
        this.settingsService.updateLocalMicLanguageOut(languageCode, micId);
      }),
      catchError((error) => {
        console.error('[QR User Service] Error in sendTargetLanguageUpdate:', error);
        return of(undefined);
      })
    );
  }

  /**
   * Saves the QR user's language preference and updates settings
   * - Soundshape: Uses WebSocket messages
   * - Microphonekit: Uses API call to /v2/settings/mics
   * @param language The language to save
   * @param skipLanguageSelection Whether to skip calling selectTranslationLanguage (if already called by caller)
   * @returns Observable that completes after updating settings
   */
  saveQrUserLanguage(language: Language, skipLanguageSelection = false): Observable<Record<string, unknown>> {
    if (!skipLanguageSelection) {
      this.languageSettings.selectTranslationLanguage(language);
    }
    this.saveLanguagePreference(language.languageCode);

    // Soundshape: Use WebSocket messages
    if (this.isSoundshapeQR()) {
      return this.sendTargetLanguageUpdate(language.languageCode).pipe(
        map(() => ({})),
        catchError((error) => {
          console.error('Error in saveQrUserLanguage:', error);
          return of({});
        })
      );
    }

    return this.settingsService.getRemoteSetSettings().pipe(
      take(1),
      switchMap((settings) => {
        if (!settings?.microphones?.length) {
          return of({});
        }

        // Create an update request for all microphones
        const micUpdate: Omit<UpdateMicrophoneSettingsRequest, 'event'> = {
          microphones: settings.microphones.map(
            (mic) =>
              ({
                mic_id: mic.mic_id,
                language_out: language.languageCode,
              }) as any
          ),
        };

        return this.settingsService.updateMicSettings(micUpdate).pipe(take(1));
      }),
      catchError((error) => {
        console.error('Error in saveQrUserLanguage:', error);
        return of({});
      })
    );
  }

  /**
   * Setup translations for the QR dialog based on detected language
   */
  private setupTranslationsForDialog(): void {
    const detectedLanguage = this.languageSettings.detectDeviceLanguage();

    if (detectedLanguage) {
      const langCode = detectedLanguage.languageCode
        .split('-')[0]
        .toLowerCase();

      this.languageSettings.setTranslocoLanguage(langCode);
    } else {
      this.languageSettings.setTranslocoLanguage('en');
    }
  }

  /**
   * Initialize QR websocket connection for QR authenticated users
   * Should be called after QR login and when entering main app screens
   */
  initializeWebsocket(): Observable<void> {
    if (!this.isGuestUser()) {
      console.warn('[QR User Service] Cannot initialize websocket: user is not a guest');
      return of(undefined);
    }

    if (this.websocketInitialized()) {
      return of(undefined);
    }

    // Use shareReplay to ensure only one initialization runs and share the result
    if (!this.initialization$) {
      this.initialization$ = from(this.performWebsocketInitialization()).pipe(
        shareReplay(1)
      );
    }

    return this.initialization$;
  }

  /**
   * Internal method to perform the actual websocket initialization
   */
  private async performWebsocketInitialization(): Promise<void> {
    // Shared-session guests use a different storage layout
    if (this.isSharedSessionUser()) {
      const jwt = this.sharedSessionAuth.getAccessToken();
      const deviceUuid = this.sharedSessionAuth.getDeviceUuid();
      if (!jwt || !deviceUuid) {
        console.warn('[QR User Service] Shared-session token or device UUID missing');
        return;
      }
      // Stash device type so isSoundshapeQR() downstream returns true
      sessionStorage.setItem(this.QR_DEVICE_TYPE_KEY, 'soundshape');
      this.qrWebsocketService.connect(jwt, deviceUuid);
      this.websocketInitialized.set(true);
      return;
    }

    // Get user ID and JWT token from localStorage
    const tokenData = localStorage.getItem('token');
    if (!tokenData) {
      console.error('[QR User Service] No token found in localStorage');
      return;
    }

    let jwtToken: string;
    try {
      const parsed = JSON.parse(tokenData);
      jwtToken = parsed.token?.accessToken;
      if (!jwtToken) {
        console.error('[QR User Service] No access token found');
        return;
      }
    } catch (error) {
      console.error('[QR User Service] Failed to parse token from localStorage:', error);
      return;
    }

    // Get device UUID - try session storage first, then fetch from API by matching firestoredb
    let deviceUuid = this.getDeviceUuid();

    if (!deviceUuid) {
      try {
        // Get the firestoredb from the session (stored during QR login)
        const firestoredb = this.getFirestoreDb();

        if (!firestoredb) {
          console.warn('[QR User Service] No firestoredb found, cannot fetch UUID');
          return;
        }

        // Get licenses - they should already be loaded by the app
        let licenses = this.orderService.licenses;

        // If not loaded yet, fetch them
        if (!licenses || licenses.length === 0) {
          licenses = await firstValueFrom(
            this.orderService.getLicenses().pipe(take(1))
          );
        }

        // Find the license that matches the firestoredb
        const matchingLicense = licenses.find((license: any) => license.firestoredb === firestoredb);

        // Resolve the single device uuid the license's transcripts arrive on
        // (multi-kit licenses carry it in uuids[0]); see licenseDeviceUuid.
        const resolvedUuid = matchingLicense ? licenseDeviceUuid(matchingLicense) : undefined;
        if (resolvedUuid) {
          deviceUuid = resolvedUuid;
          this.storeDeviceUuid(deviceUuid);
        } else {
          console.warn('[QR User Service] No matching license found for firestoredb:', firestoredb);
        }
      } catch (error) {
        console.error('[QR User Service] Failed to fetch UUID from license data:', error);
      }
    }

    if (!deviceUuid) {
      console.warn('[QR User Service] No device UUID available, cannot initialize WebSocket connection');
      return;
    }

    // Connect to websocket with uuid
    this.qrWebsocketService.connect(jwtToken, deviceUuid);
    this.websocketInitialized.set(true);
  }

  /**
   * Store firestoredb for QR users
   * @param firestoredb Firestore database ID
   */
  storeFirestoreDb(firestoredb: string): void {
    sessionStorage.setItem('qr_firestoredb', firestoredb);
  }

  /**
   * Get stored firestoredb for QR users
   * @returns Firestore database ID or null if not found
   */
  getFirestoreDb(): string | null {
    return sessionStorage.getItem('qr_firestoredb');
  }


  /**
   * Disconnect QR websocket
   * Should be called on logout or when QR session expires
   */
  disconnectWebsocket(): void {
    if (!this.websocketInitialized()) {
      return;
    }

    // Tear down read-along before the WS closes so the proxy receives
    // tts.read_along.unsubscribe and drops our Subscriber cleanly.
    this.ttsAudioService.setEnabled(false);
    this.qrWebsocketService.disconnect();
    this.websocketInitialized.set(false);
    // Drop the cached initializer so a rejoin in the same tab re-runs it; the
    // stale shareReplay(1) has already completed and would otherwise short-
    // circuit initializeWebsocket() without reconnecting.
    this.initialization$ = null;
  }

  /**
   * Check if QR websocket is currently connected
   */
  isWebsocketConnected(): boolean {
    return this.qrWebsocketService.isConnected();
  }

  /**
   * Store device UUID for QR users
   * @param uuid Device UUID
   */
  storeDeviceUuid(uuid: string): void {
    sessionStorage.setItem(this.QR_DEVICE_UUID_KEY, uuid);
  }

  /**
   * Get stored device UUID for QR users
   * @returns UUID or null if not found
   */
  getDeviceUuid(): string | null {
    // QR guests store under qr_device_uuid (sessionStorage); shared-session guests
    // store under shared_session_device_uuid (localStorage, via SharedSessionAuthService).
    // Fall back to the latter so branding/UUID consumers work for shared-session guests too.
    return (
      sessionStorage.getItem(this.QR_DEVICE_UUID_KEY) ??
      this.sharedSessionAuth.getDeviceUuid()
    );
  }

  /**
   * Clear stored device UUID
   */
  clearDeviceUuid(): void {
    sessionStorage.removeItem(this.QR_DEVICE_UUID_KEY);
  }

  /**
   * Determine and store the device type based on license description.
   * Call this after firestoredb is stored.
   */
  async determineAndStoreDeviceType(): Promise<void> {
    const firestoredb = this.getFirestoreDb();
    if (!firestoredb) return;

    let licenses = this.orderService.licenses;
    if (!licenses || licenses.length === 0) {
      licenses = await firstValueFrom(this.orderService.getLicenses().pipe(take(1)));
    }

    const matchingLicense = licenses.find((l: any) => l.firestoredb === firestoredb);
    if (matchingLicense) {
      const isSoundshape = matchingLicense.description?.toLowerCase().includes('soundshape');
      sessionStorage.setItem(this.QR_DEVICE_TYPE_KEY, isSoundshape ? 'soundshape' : 'microphonekit');
    }
  }

  /**
   * Check if current QR user is on a Soundshape device
   * @returns true if Soundshape, false otherwise
   */
  isSoundshapeQR(): boolean {
    if (this.isSharedSessionUser()) return true;
    if (!this.isQrUser()) return false;
    return sessionStorage.getItem(this.QR_DEVICE_TYPE_KEY) === 'soundshape';
  }

  /**
   * Check if transcript is missing for target language and retry once if needed.
   * Only retries if translations are enabled for the guest.
   */
  checkAndRetryTranscript(transcripts: { [key: string]: string }, targetLanguage: string): void {
    if (this.transcriptRetryAttempted()) return;
    if (this.guestTranslationsEnabled() === false) return;

    const hasTranscript = this.hasTranscriptForLanguage(transcripts, targetLanguage);
    if (hasTranscript) return;

    this.transcriptRetryAttempted.set(true);
    setTimeout(() => {
      this.sendTargetLanguageUpdate(targetLanguage).pipe(take(1)).subscribe();
    }, 1500);
  }

  private hasTranscriptForLanguage(transcripts: { [key: string]: string }, targetLang: string): boolean {
    if (!transcripts || !targetLang) return true; // Assume OK if no data

    // Exact match or short code match
    if (transcripts[targetLang]) return true;
    const shortCode = targetLang.split('-')[0];
    return Object.keys(transcripts).some(key => key === shortCode || key.startsWith(shortCode + '-'));
  }
}
